import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

export default function AdminPayouts() {
  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState("pending");
  const [period, setPeriod] = useState("month");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    const params = {};
    if (status) params.status = status;
    const now = new Date();
    if (period === "week") {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      params.from_date = d.toISOString();
    } else if (period === "month") {
      params.from_date = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    } else if (period === "year") {
      params.from_date = new Date(now.getFullYear(), 0, 1).toISOString();
    }
    api.get("/admin/payouts", { params }).then((r) => setGroups(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, period]);

  const togglePaid = async (line, isPaid) => {
    setBusy(true);
    try {
      const url = isPaid ? "/admin/payouts/mark-paid" : "/admin/payouts/mark-unpaid";
      await api.patch(url, { order_id: line.order_id, item_index: line.item_index });
      toast.success(isPaid ? "Marcado como pagado" : "Marcado como pendiente");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const markMonth = async (artist_id) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    if (!window.confirm(`Marcar como pagadas todas las líneas pendientes de este mes para este artista?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/mark-month", { artist_id, year, month });
      toast.success(`${data.items_marked} líneas marcadas como pagadas`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sendMonthlySummary = async (artist_id, mark_as_paid = false) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const targetLabel = artist_id ? "este artista" : "TODOS los artistas con movimientos este mes";
    const action = mark_as_paid ? "enviar email y marcar como pagados" : "enviar email (sin marcar como pagados)";
    if (!window.confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} a ${targetLabel}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/send-month-summary", { year, month, artist_id: artist_id || null, mark_as_paid });
      toast.success(`Enviados ${data.emails_sent} emails de ${data.artists_processed} artistas`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const grandTotalPending = groups.reduce((s, g) => s + (g.pending_total || 0), 0);
  const grandTotalPaid = groups.reduce((s, g) => s + (g.paid_total || 0), 0);

  return (
    <div data-testid="admin-payouts">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-2">Liquidaciones a artistas</h1>
      <p className="text-sm text-neutral-500 mb-6">Pagos netos pendientes y pagados, agrupados por artista. El IRPF mostrado es informativo (configurable por artista o por defecto en Ajustes).</p>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-neutral-200 p-4">
          <div className="overline text-[10px] text-neutral-500">Pendiente total</div>
          <div className="font-serif text-3xl mt-1 text-amber-700">€{grandTotalPending.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-neutral-200 p-4">
          <div className="overline text-[10px] text-neutral-500">Pagado</div>
          <div className="font-serif text-3xl mt-1 text-emerald-700">€{grandTotalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-neutral-200 p-4">
          <div className="overline text-[10px] text-neutral-500">Artistas con saldo</div>
          <div className="font-serif text-3xl mt-1">{groups.length}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="flex gap-1">
          {[
            { k: "pending", label: "Pendientes" },
            { k: "paid", label: "Pagadas" },
            { k: "", label: "Todas" },
          ].map((p) => (
            <button key={p.k || "all"} onClick={() => setStatus(p.k)} className={`overline text-xs px-3 py-2 border ${status === p.k ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-50"}`} data-testid={`payout-status-${p.k || "all"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {[
            { k: "week", label: "Semana" },
            { k: "month", label: "Mes" },
            { k: "year", label: "Año" },
            { k: "all", label: "Todo" },
          ].map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)} className={`overline text-xs px-3 py-2 border ${period === p.k ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-50"}`} data-testid={`payout-period-${p.k}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => sendMonthlySummary(null, false)} disabled={busy} className="overline text-xs px-3 py-2 border border-black hover:bg-neutral-50 disabled:opacity-50" data-testid="send-all-summaries">
            Enviar resúmenes del mes
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.artist_id} className="bg-white border border-neutral-200" data-testid={`payout-group-${g.artist_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-neutral-100">
              <div>
                <div className="font-serif text-2xl">{g.artist_name}</div>
                <div className="text-xs text-neutral-500 mt-1">
                  {g.artist_email} {g.tax_id && `· NIF ${g.tax_id}`} {g.is_business ? "· autónomo" : "· no autónomo"}
                </div>
              </div>
              <div className="flex items-end gap-6">
                <div className="text-right">
                  <div className="overline text-[10px] text-neutral-500">Pendiente bruto</div>
                  <div className="font-serif text-2xl text-amber-700">€{g.pending_total?.toFixed(2)}</div>
                </div>
                <div className="text-right">
                  <div className="overline text-[10px] text-neutral-500">IRPF {(g.irpf_pct * 100).toFixed(0)}%</div>
                  <div className="font-serif text-2xl text-neutral-500">−€{g.pending_irpf?.toFixed(2)}</div>
                </div>
                <div className="text-right">
                  <div className="overline text-[10px] text-neutral-500">Neto a pagar</div>
                  <div className="font-serif text-3xl text-emerald-700">€{g.pending_net_to_pay?.toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div className="px-5 py-2 border-b border-neutral-100 flex items-center justify-between">
              <button onClick={() => setExpanded((e) => ({ ...e, [g.artist_id]: !e[g.artist_id] }))} className="overline text-xs text-neutral-500 hover:text-black" data-testid={`expand-${g.artist_id}`}>
                {expanded[g.artist_id] ? "Ocultar líneas" : `Ver ${g.lines.length} línea(s)`}
              </button>
              <div className="flex gap-2">
                <button onClick={() => markMonth(g.artist_id)} disabled={busy || g.pending_total <= 0} className="overline text-xs px-3 py-1.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-30" data-testid={`mark-month-${g.artist_id}`}>
                  Marcar mes pagado
                </button>
              </div>
            </div>
            {expanded[g.artist_id] && (
              <table className="brutal-table w-full text-sm">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Obra</th>
                    <th>Talla</th>
                    <th>Cant.</th>
                    <th>Neto</th>
                    <th>Estado</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {g.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="font-mono text-xs">#{l.order_short}</td>
                      <td>{l.title}</td>
                      <td className="text-xs">{l.size_label}</td>
                      <td className="text-xs">{l.quantity}</td>
                      <td className="font-medium">€{l.net_total.toFixed(2)}</td>
                      <td>
                        {l.payout_status === "paid" ? (
                          <span className="overline text-[10px] px-2 py-1 border border-emerald-200 bg-emerald-50 text-emerald-700">
                            Pagado {l.payout_paid_at && new Date(l.payout_paid_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="overline text-[10px] px-2 py-1 border border-amber-200 bg-amber-50 text-amber-700">Pendiente</span>
                        )}
                      </td>
                      <td>
                        {l.payout_status === "paid" ? (
                          <button onClick={() => togglePaid(l, false)} disabled={busy} className="overline text-[10px] px-2 py-1 border border-neutral-300 hover:bg-neutral-50" data-testid={`unmark-${l.order_id}-${l.item_index}`}><X size={11} /></button>
                        ) : (
                          <button onClick={() => togglePaid(l, true)} disabled={busy} className="overline text-[10px] px-2 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid={`mark-${l.order_id}-${l.item_index}`}><Check size={11} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
        {groups.length === 0 && <div className="bg-white border border-neutral-200 py-12 text-center overline text-neutral-400">Sin movimientos en este periodo</div>}
      </div>
    </div>
  );
}
